import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  RotateCcwIcon as RestoreIcon,
  UserPlusIcon,
  UserCheckIcon,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { avatarInitials, avatarColor } from "@/lib/avatar";
import { useDriverMutations } from "./useDriverMutations";
import { DriverFormSheet } from "./DriverFormSheet";
import { CreateDriverUserSheet } from "./CreateDriverUserSheet";

const PAGE_SIZE = 25;

type DriverStatus = "active" | "sick" | "on_vacation" | "art" | "suspended"

const DRIVER_STATUS_LABEL: Record<DriverStatus, string> = {
  active:      "Activo",
  sick:        "Enfermo",
  on_vacation: "Vacaciones",
  art:         "ART",
  suspended:   "Suspendido",
}

const DRIVER_STATUS_CLASS: Record<DriverStatus, string> = {
  active:      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  sick:        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  on_vacation: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  art:         "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  suspended:   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

interface DriverRow {
  id: string;
  code: string;
  name: string;
  sgtCodes: string[] | null;
  fileId: string | null;
  dni: string | null;
  docTypeId: string | null;
  docId: string | null;
  docTypeName: string | null;
  docTypeCode: string | null;
  isOwned: boolean;
  pvcuenta: string | null;
  userId: string | null;
  operatorId: string | null;
  operatorName: string | null;
  driverStatus: DriverStatus;
  phoneCdc: string | null;
  phonePersonal: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = "active" | "deleted" | "all";
type FilterOwned = "all" | "owned" | "external";
type FilterOperator = string[];



interface Props {
  defaultSearch?: string;
}

export function DriversTable({ defaultSearch = "" }: Props) {
  const { can } = usePermissions();

  const [searchInput, setSearchInput]   = useState(defaultSearch);
  const [search, setSearch]             = useState(defaultSearch);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active");
  const [filterOwned, setFilterOwned]   = useState<FilterOwned>("all");
  const [page, setPage]                 = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sheetOpen, setSheetOpen]       = useState(false);
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [editing, setEditing]           = useState<DriverRow | null>(null);
  const [deleting, setDeleting]         = useState<DriverRow | null>(null);
  const [formError, setFormError]         = useState<string | null>(null);
  const [filterSgt, setFilterSgt]         = useState<"all" | "with" | "without">("all");
  const [filterOperator, setFilterOperator] = useState<FilterOperator>([]);
  const [createUserDriver, setCreateUserDriver] = useState<DriverRow | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterOwned, filterSgt, filterOperator]);

  interface DriversResponse { data: DriverRow[]; total: number; page: number; pageSize: number; }

  const { data: response, isLoading } = useQuery<DriversResponse>({
    queryKey: ["drivers", { status: filterStatus, owned: filterOwned, search, page }],
    queryFn: async () => {
      const query: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
      if (filterStatus === "active")  query.deleted = "false";
      if (filterStatus === "deleted") query.deleted = "true";
      if (filterOwned === "owned")    query.isOwned = "true";
      if (filterOwned === "external") query.isOwned = "false";
      if (search.trim())              query.search  = search.trim();

      const res = await api.providers.drivers.$get({ query } as any);
      return res.json() as Promise<DriversResponse>;
    },
  });

  const driversData  = response?.data ?? [];
  const total        = response?.total ?? 0;
  const totalPages   = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paged        = driversData;

  // Operators derived from current page — used for avatar display only
  const uniqueOperators = useMemo(() => {
    const ops = new Map<string, string>();
    driversData.forEach(d => {
      if (d.operatorId && d.operatorName && !ops.has(d.operatorId)) {
        ops.set(d.operatorId, d.operatorName);
      }
    });
    return Array.from(ops.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [driversData]);

  const { createMut, updateMut, deleteMut, restoreMut, isSaving } = useDriverMutations({
    onSuccess: () => {
      setSheetOpen(false);
      setDeleteOpen(false);
      setEditing(null);
      setDeleting(null);
      setFormError(null);
    },
    onError: (err) => setFormError(err.message),
  });

  function openCreate() {
    setEditing(null);
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(item: DriverRow) {
    setEditing(item);
    setFormError(null);
    setSheetOpen(true);
  }

  function handleSave(id: string | null, data: any) {
    if (id) {
      updateMut.mutate({ id, data });
    } else {
      createMut.mutate(data);
    }
  }

  return (
    <div className="space-y-4 w-full h-full flex flex-col flex-1">
      <div className="flex flex-col gap-3 flex-shrink-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar chofer, código, DNI u operador..."
              className="pl-8 w-full sm:w-64"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => { setSearch(e.target.value); setPage(1); }, 350);
              }}
            />
          </div>

          <Select value={filterOwned} onValueChange={(v: any) => { setFilterOwned(v); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">todos los choferes</SelectItem>
              <SelectItem value="owned">propios (CDC)</SelectItem>
              <SelectItem value="external">externos</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterSgt} onValueChange={(v: any) => { setFilterSgt(v); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue>
                {filterSgt === "all" ? "Todos (SGT)" : filterSgt === "with" ? "Con SGT" : "Sin SGT"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (SGT)</SelectItem>
              <SelectItem value="with">Con SGT</SelectItem>
              <SelectItem value="without">Sin SGT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v: any) => { setFilterStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">activos</SelectItem>
              <SelectItem value="deleted">borrados</SelectItem>
              <SelectItem value="all">todos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {can("write", "drivers") && (
          <Button onClick={openCreate} className="gap-2">
            <PlusIcon className="size-4" /> Nuevo chofer
          </Button>
        )}
      </div>

      {uniqueOperators.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground shrink-0">Operadores:</span>
          {uniqueOperators.map(([id, name]) => (
            <button
              key={id}
              onClick={() => {
                setFilterOperator(prev =>
                  prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                );
                setPage(1);
              }}
              className={`h-7 flex items-center gap-1.5 text-xs px-2 rounded-full font-medium transition-colors ${
                filterOperator.includes(id)
                  ? "text-white"
                  : "bg-background text-muted-foreground border border-border hover:bg-muted"
              }`}
              style={filterOperator.includes(id) ? { backgroundColor: avatarColor(name) } : undefined}
            >
              <Avatar size="sm">
                <AvatarFallback
                  className="text-white text-[10px] font-semibold"
                  style={{ backgroundColor: avatarColor(name) }}
                >
                  {avatarInitials(name)}
                </AvatarFallback>
              </Avatar>
              {name}
            </button>
          ))}
          {filterOperator.length > 0 && (
            <button
              onClick={() => { setFilterOperator([]); setPage(1); }}
              className="h-7 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              limpiar
            </button>
          )}
        </div>
      )}
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>DNI / Doc</TableHead>
              <TableHead>Flota</TableHead>
              <TableHead>Operador</TableHead>
              <TableHead className="w-28 text-center">Estado</TableHead>
              <TableHead className="w-40">Tel. CDC</TableHead>
              <TableHead className="w-40">Tel. Personal</TableHead>
              <TableHead>SGT Codes</TableHead>
              {can("write", "drivers") && <TableHead className="w-[100px] text-right pr-4">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={can("write", "drivers") ? 10 : 9} className="h-24 text-center text-muted-foreground">
                  Cargando choferes...
                </TableCell>
              </TableRow>
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={can("write", "drivers") ? 10 : 9} className="h-24 text-center text-muted-foreground">
                  No se encontraron choferes.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((item) => (
                <TableRow key={item.id} className={item.deletedAt ? "opacity-50 italic" : ""}>
                  <TableCell className="font-mono font-medium text-xs">
                    <span className="bg-muted px-1.5 py-0.5 rounded">{item.code}</span>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{item.name}</div>
                    {item.fileId && <div className="text-[10px] text-muted-foreground">Legajo: {item.fileId}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{item.dni || "—"}</div>
                    <div className="text-[10px] text-muted-foreground/60">
                      {item.docTypeCode} {item.docId}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.isOwned ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 font-medium">Propio</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">Externo</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.operatorName ? (
                      <span className="flex items-center gap-1.5">
                        <Avatar size="sm" title={item.operatorName}>
                          <AvatarFallback className="text-white text-[10px] font-semibold" style={{ backgroundColor: avatarColor(item.operatorName) }}>
                            {avatarInitials(item.operatorName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{item.operatorName}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`text-[10px] px-1.5 py-0 rounded font-medium ${DRIVER_STATUS_CLASS[item.driverStatus ?? "active"]}`}>
                      {DRIVER_STATUS_LABEL[item.driverStatus ?? "active"]}
                    </span>
                  </TableCell>
                  <TableCell>
                    {item.phoneCdc || "—"}
                  </TableCell>
                  <TableCell>
                    {item.phonePersonal || "—"}
                  </TableCell>
                  <TableCell>
                    {item.sgtCodes && item.sgtCodes.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {item.sgtCodes.map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded-sm bg-muted font-mono text-[10px] text-muted-foreground">
                            {c}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  {can("write", "drivers") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {item.deletedAt ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => restoreMut.mutate(item.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            {item.userId ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium"
                                title="Tiene acceso al sistema"
                              >
                                <UserCheckIcon className="size-3" /> Acceso
                              </span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setCreateUserDriver(item)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Crear usuario"
                              >
                                <UserPlusIcon className="size-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => { setDeleting(item); setDeleteOpen(true); }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      <CreateDriverUserSheet
        driver={createUserDriver}
        onClose={() => setCreateUserDriver(null)}
      />

      <DriverFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        editing={editing}
        onSave={handleSave}
        isSaving={isSaving}
        error={formError}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar chofer</DialogTitle>
            <DialogDescription>
              ¿Eliminás al chofer <strong>{deleting?.name}</strong>? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
